import { produce } from "immer";
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { cn, generateId } from "../utils";
import type { Node, NodeEditorProps, NodeEditorRef } from "./dto";
import { useControllableValue } from "ahooks";
import { cloneDeep } from "lodash";

const INPUT_TYPE = {
  INSERT: "insertText",
  DELETE: "deleteContentBackward",
  ENTER: "insertParagraph",
};

// 获取光标位置的节点和偏移量
function getCursorNodeAndOffset() {
  const selection = window.getSelection();
  if (selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const startNode = range.startContainer;
  const { startOffset } = range;

  return {
    ele: (startNode.nodeType === 3
      ? startNode.parentElement
      : startNode) as HTMLElement,
    offset: startOffset,
  };
}
// 更新光标位置
function setCursorPosition(element: HTMLElement, position: number) {
  try {
    // 创建Range和Selection对象
    const range = document.createRange();
    let node = element;
    // 如果节点是文本节点，则设置内部文本节点光标位置
    if (
      element.childNodes.length === 1 &&
      element.childNodes[0].nodeType === 3
    ) {
      node = element.childNodes[0] as HTMLElement;
    }
    range.setStart(node, position);
    range.setEnd(node, position);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    element.focus(); // 确保元素获得焦点
  } catch (e) {
    console.error(`【NodeEditor】setCursorPosition error: ${e.message}`, e);
  }
}
// 创建合并相邻的节点方法
function createMergeAdjacentNodesFn(
  mergeable: (a: Node, b: Node) => boolean,
  callback?: (prev: Node, cur: Node, idx: number) => void
) {
  return function (nodes: Node[]) {
    const ns = cloneDeep(nodes);
    const newNodes: Node[] = [];
    for (let i = 0; i < ns.length; i++) {
      const currentNode = ns[i];
      if (!currentNode.type) continue;
      const prevNode = newNodes[newNodes.length - 1];
      const isAdjacent =
        newNodes.length > 0 && mergeable(prevNode, currentNode);
      if (isAdjacent) {
        callback?.(prevNode, currentNode, newNodes.length - 1);
        prevNode.content += currentNode.content;
      } else {
        newNodes.push(currentNode);
      }
    }
    return newNodes;
  };
}

const NodeEditor = forwardRef(
  (props: NodeEditorProps, ref: ForwardedRef<NodeEditorRef>) => {
    const {
      interactionMode = "editable",
      className,
      style,
      nodeClassName,
      nodeStyle,
      mergeable,
      renderTagNode,
      onNodeClick,
    } = props;
    const [nodes, setNodes] = useControllableValue<Node[]>(props, {
      defaultValue: [],
    });
    // 只读
    const readonly = interactionMode === "disabled";
    // 只移动光标
    const cursorOnly = interactionMode === "cursorOnly";

    const _nodes = nodes ?? [];
    // 是否正在拼音输入
    const isComposingRef = useRef(false);
    // 光标位置
    const selectionRef = useRef<{
      id: string | number;
      offset: number;
      content?: string;
    }>();
    // 编辑器元素
    const textareaRef = useRef<HTMLDivElement>(null);
    // 用于强制更新
    const [key, setKey] = useState(0);
    // 合并相邻的文本节点 并更新光标位置
    const mergeAdjacentTextNodes = createMergeAdjacentNodesFn(
      (a, b) => a.type === "text" && b.type === "text",
      (prev, cur) => {
        const { id, offset } = selectionRef.current;
        if (id === -1) {
          selectionRef.current = {
            id: prev.id,
            offset: prev.content.length + cur.content.length,
          };
        } else if (id === cur.id) {
          selectionRef.current = {
            id: prev.id,
            offset: prev.content.length + offset,
          };
        }
      }
    );
    // 外部合并相邻的节点逻辑处理
    const outerMeragleAdjacentNodes = mergeable
      ? createMergeAdjacentNodesFn(mergeable, (prev, cur, idx) => {
          const { id, offset } = selectionRef.current;
          if (prev.type === "text" && id !== -1) {
            // 只有text节点且光标在被合并的节点内再更新光标位置
            if (id === cur.id) {
              selectionRef.current = {
                id: prev.id,
                offset: prev.content.length + offset,
              };
            }
          } else {
            selectionRef.current = {
              id: -1,
              offset: idx + 1,
            };
          }
        })
      : (ns: Node[]) => ns;
    // 合并相邻的节点
    const mergeNodes = (ns: Node[]) =>
      outerMeragleAdjacentNodes(mergeAdjacentTextNodes(ns));
    // 更新光标位置
    const updateSelection = () => {
      const selection = getCursorNodeAndOffset();
      // composing状态下不更新光标位置
      if (!selection || isComposingRef.current) return;
      const { ele, offset } = selection;
      if (!textareaRef.current.contains(ele)) return;
      const id = ele.getAttribute("data-id");
      const selectNodeIdx = _nodes.findIndex((i) => i.id === id);
      if (ele === textareaRef.current) {
        selectionRef.current = {
          id: -1,
          offset,
        };
      } else if (id && selectNodeIdx !== -1) {
        const selectNode = _nodes[selectNodeIdx];
        selectionRef.current =
          selectNode.type === "tag"
            ? {
                id: -1,
                offset: selectNodeIdx + 1,
                content: "",
              }
            : {
                id,
                offset,
                content: window.getSelection().toString(),
              };
      }
    };

    // 在光标处插入节点
    const insertNodeAtCursor = (node: Omit<Node, "id">) => {
      const defalutSelection: typeof selectionRef.current = {
        id: -1,
        offset: _nodes.length,
      };
      const {
        id,
        offset,
        content = "",
      } = selectionRef.current ?? defalutSelection;
      // 如果在根插入节点
      if (id === -1) {
        selectionRef.current = {
          id: -1,
          offset: offset + 1,
        };
        return setNodes(
          mergeNodes(
            produce(_nodes, (draft) => {
              draft.splice(offset, 0, {
                ...node,
                id: generateId(),
              });
            })
          )
        );
      }
      /* =============== 这边为在text节点中间插入新节点 ============*/
      // 需要插入的节点
      const newNodes = [];
      const orignalText = _nodes.find((i) => i.id === id)?.content ?? "";
      const startText = orignalText.slice(0, offset);
      const endText = orignalText.slice(offset);
      // 新插入的节点为text类型 则合并text节点
      if (node.type === "text") {
        newNodes.push({
          id,
          type: "text",
          content: `${startText}${node.content}${endText.slice(
            content.length
          )}`,
        });
        // 如果有选中的文字则需要删除掉
        selectionRef.current = {
          id,
          offset: `${startText}${node.content}`.length,
        };
      }
      // 新插入的节点为tag类型 则拆分text节点
      if (node.type === "tag") {
        const tagId = generateId();
        const endId = generateId();
        // 非开头切割则插入前半部分分割的节点
        if (offset !== 0) {
          newNodes.push({
            id,
            type: "text",
            content: startText,
          });
        }
        // 新插入的节点
        newNodes.push({
          id: tagId,
          ...node,
        });
        // 非结尾切割则插入后半部分分割的节点
        if (offset !== orignalText.length) {
          newNodes.push({
            id: endId,
            type: "text",
            content: endText,
          });
          selectionRef.current = {
            id: endId,
            offset: 0,
          };
        } else {
          // 光标在结尾，插入新节点后光标移动到新节点后面
          const newOffset = _nodes.findIndex((i) => i.id === id) + 2;
          selectionRef.current = {
            id: -1,
            offset: newOffset,
          };
        }
      }
      setNodes(
        mergeNodes(
          produce(_nodes, (draft) => {
            draft.splice(
              _nodes.findIndex((i) => i.id === id),
              1,
              ...newNodes
            );
          })
        )
      );
    };
    // 在光标处删除节点
    const deleteNodeAtCursor = () => {
      const defalutSelection = {
        id: -1,
        offset: _nodes.length,
      };
      const { id, offset } = selectionRef.current ?? defalutSelection;
      // 需要删除的节点id
      const deleteId = id === -1 ? _nodes[offset - 1]?.id : id;
      if (!deleteId) return setCursorPosition(textareaRef.current, 0);
      // 删除后根光标位置
      const newOffset =
        id === -1 ? offset - 1 : _nodes.findIndex((i) => i.id === id);
      if (deleteId) {
        selectionRef.current = {
          id: -1,
          offset: newOffset,
        };
        setNodes(
          mergeNodes(
            produce(_nodes, (draft) => {
              const index = draft.findIndex((i) => i.id === deleteId);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            })
          )
        );
      }
    };

    useImperativeHandle(ref, () => ({
      insertNodeAtCursor,
      deleteNodeAtCursor,
    }));

    // 节点渲染后更新光标位置
    useEffect(() => {
      if (!selectionRef.current) return;
      let { id, offset } = selectionRef.current;
      let node: HTMLElement = textareaRef.current;
      const nextNode = _nodes[offset];
      if (id === -1 && nextNode?.type === "text") {
        id = nextNode.id;
        offset = 0;
        node = textareaRef.current.querySelector(
          `[data-id="${nextNode.id}"]`
        ) as HTMLSpanElement;
      }
      if (id !== -1) {
        node = textareaRef.current.querySelector(
          `[data-id="${id}"]`
        ) as HTMLSpanElement;
      }
      setCursorPosition(node, offset);
    }, [JSON.stringify(_nodes), key]);

    // 输入内容
    const handleChange = (
      input: string,
      target: HTMLDivElement,
      type = INPUT_TYPE.INSERT
    ) => {
      switch (type) {
        // 正常输入
        case INPUT_TYPE.INSERT: {
          insertNodeAtCursor({
            type: "text",
            content: input,
          });
          break;
        }
        // 删除键
        case INPUT_TYPE.DELETE: {
          updateSelection();
          const childrenList = Array.from(target.children) as HTMLSpanElement[];
          setNodes(
            mergeNodes([
              ...childrenList.map((i) => {
                const t = i.getAttribute("data-type") as Node["type"];
                return {
                  type: t,
                  content:
                    t === "tag" ? i.getAttribute("data-value") : i.innerText,
                  id: i.getAttribute("data-id"),
                };
              }),
            ])
          );
          break;
        }
        // 回车键插入\n
        case INPUT_TYPE.ENTER: {
          insertNodeAtCursor({
            type: "text",
            content: "\n",
          });
          break;
        }
        default:
          break;
      }
      setKey((k) => k + 1);
    };

    const renderNode = (node: Node) => {
      if (node.type === "text") return node.content;
      if (node.type === "tag")
        return renderTagNode ? renderTagNode(node) : node.content;
    };

    return (
      <div
        key={key}
        ref={textareaRef}
        contentEditable={!readonly}
        onInput={(e) => {
          if (isComposingRef.current) return;
          // 新输入的内容
          const { data, inputType } = e.nativeEvent as InputEvent;
          handleChange(data, e.currentTarget, inputType);
        }}
        onPaste={(e) => {
          // 阻止默认粘贴行为
          e.preventDefault();
          // cursorOnly 下不可粘贴内容
          if (cursorOnly) return;
          // 获取粘贴板内容
          const pastedText = e.clipboardData.getData("text");
          handleChange(pastedText, e.currentTarget);
        }}
        onBeforeInput={(e) => {
          if (cursorOnly) {
            e.preventDefault();
          }
        }}
        onKeyDown={(e) => {
          // cursorOnly 下不可通过按键删除元素
          if (e.key === "Backspace" && cursorOnly) {
            e.preventDefault();
          }
        }}
        onCompositionStart={() => {
          isComposingRef.current = true;
          if (cursorOnly) {
            const selection = getCursorNodeAndOffset();
            if (!selection) return;
            const { ele, offset } = selection;
            if (!textareaRef.current.contains(ele)) return;
            if (ele === textareaRef.current) {
              selectionRef.current = {
                id: -1,
                offset,
              };
            }
          }
        }}
        onCompositionEnd={(e) => {
          if (cursorOnly) {
            setKey((k) => k + 1);
            setNodes([..._nodes]);
            return;
          }
          isComposingRef.current = false;
          // 新输入的内容
          const input = e.nativeEvent.data;
          handleChange(input, e.currentTarget);
        }}
        onSelect={() => {
          updateSelection();
        }}
        className={cn(
          "wbuc-whitespace-break-spaces wbuc-rounded-[0.25rem] wbuc-border wbuc-border-solid wbuc-border-[rgba(25,28,38,0.1)] wbuc-bg-[#FFFFFF] wbuc-p-2 wbuc-outline-none",
          className
        )}
        style={style}
        role="textbox"
        suppressContentEditableWarning
      >
        {_nodes.map((node) => {
          return (
            <span
              key={node.id}
              className={cn(
                "wbuc-mr-[0.625rem] wbuc-break-all",
                node.type === "tag" &&
                  "wbuc-rounded-[0.125rem] wbuc-border wbuc-border-solid wbuc-border-[rgba(25,28,38,0.1)] wbuc-bg-[#F9FAFB] wbuc-px-[0.375rem]",
                nodeClassName?.(node)
              )}
              style={nodeStyle?.(node)}
              contentEditable={node.type === "text" && !readonly}
              data-id={node.id}
              data-type={node.type}
              data-value={node.content}
              suppressContentEditableWarning
              onClick={() => {
                onNodeClick?.(node);
              }}
            >
              {renderNode(node)}
            </span>
          );
        })}
      </div>
    );
  }
);

export default NodeEditor;
